import React, { useState, useMemo } from 'react';
import { ChevronLeft, ChevronRight, Calendar, XCircle } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';

import { useSubscriptionCalendar } from '@/lib/api/analytics';
import { formatArm, splitBreakdown, splitDayAttempts } from '@/lib/subscription-retry-split';
import { useStatsFilters } from '../context/StatsFiltersContext';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Button } from '@/components/ui/button';
import { useTranslation } from 'react-i18next';

interface CalendarDayInfo {
  date: Date;
  day: number;
  isCurrentMonth: boolean;
  dateString: string;
}

interface CalendarDayData {
  date: string;
  upcoming_billing: { count: number };
  planned_cancellations: { count: number };
  successful_payments: { count: number; retry_breakdown: Record<number, number> };
  preauth_payments: { count: number; retry_breakdown: Record<number, number> };
  failed_payments: { count: number; retry_breakdown: Record<number, number> };
  subscription_canceled: { count: number };
}

export function SubscriptionCalendar() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { selectedStores, selectedCurrency } = useStatsFilters();
  const [currentDate, setCurrentDate] = useState(new Date());
  const { t, i18n } = useTranslation('analytics');

  // State to track visibility of each metric type
  const [visibleMetrics, setVisibleMetrics] = useState({
    upcoming_billing: true,
    planned_cancellations: true,
    successful_payments: true,
    preauth_payments: true,
    failed_payments: true,
    subscription_canceled: true,
  });

  // Fetch calendar data
  const { data, isLoading, isError, error } = useSubscriptionCalendar({
    storeIds: selectedStores,
    currency: selectedCurrency,
    monthDate: currentDate,
  });

  // Debug: Log data received
  React.useEffect(() => {
    console.log('[Calendar UI] Filters:', {
      selectedStores,
      selectedCurrency,
      currentMonth: currentDate.toISOString().split('T')[0],
    });

    if (data) {
      console.log('[Calendar UI] Received data:', {
        keys: Object.keys(data).length,
        sampleKeys: Object.keys(data).slice(0, 5),
        sampleData: Object.keys(data).length > 0 ? data[Object.keys(data)[0]] : null,
      });
    }
  }, [data, selectedStores, selectedCurrency, currentDate]);

  // Convert data to a map for easy lookup
  const calendarData = useMemo(() => {
    if (!data) return new Map<string, CalendarDayData>();

    const dataMap = new Map<string, CalendarDayData>();
    Object.entries(data).forEach(([dateString, dayData]) => {
      dataMap.set(dateString, dayData as CalendarDayData);
    });
    return dataMap;
  }, [data]);

  // Toggle visibility of a specific metric
  const toggleMetricVisibility = (metricKey: keyof typeof visibleMetrics) => {
    setVisibleMetrics(prev => ({
      ...prev,
      [metricKey]: !prev[metricKey]
    }));
  };

  // Navigation handlers
  const goToPreviousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  // Handle cell click
  const handleCellClick = (dateString: string, isCurrentMonth: boolean) => {
    if (!isCurrentMonth) return;

    const dayData = calendarData.get(dateString);
    if (dayData && (
      dayData.upcoming_billing.count > 0 ||
      dayData.planned_cancellations.count > 0 ||
      dayData.successful_payments.count > 0 ||
      dayData.preauth_payments.count > 0 ||
      dayData.failed_payments.count > 0 ||
      dayData.subscription_canceled.count > 0
    )) {
      const params = new URLSearchParams(searchParams.toString());
      params.set('date', dateString);
      setSearchParams(params, { replace: true });
    }
  };

  // Get calendar grid data
  const getCalendarDays = (): CalendarDayInfo[] => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();

    const firstDayOfMonth = new Date(year, month, 1);
    const lastDayOfMonth = new Date(year, month + 1, 0);
    const daysInMonth = lastDayOfMonth.getDate();

    const startDayOfWeek = firstDayOfMonth.getDay();
    const daysFromPrevMonth = startDayOfWeek;
    const prevMonth = new Date(year, month, 0);
    const daysInPrevMonth = prevMonth.getDate();

    const createDateString = (year: number, month: number, day: number) => {
      return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    };

    const days: CalendarDayInfo[] = [];

    // Previous month days
    for (let i = daysFromPrevMonth - 1; i >= 0; i--) {
      const day = daysInPrevMonth - i;
      const date = new Date(year, month - 1, day);
      days.push({
        date,
        day,
        isCurrentMonth: false,
        dateString: createDateString(year, month - 1, day),
      });
    }

    // Current month days
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      days.push({
        date,
        day,
        isCurrentMonth: true,
        dateString: createDateString(year, month, day),
      });
    }

    // Next month days to fill grid
    const totalCells = Math.ceil(days.length / 7) * 7;
    const remainingCells = totalCells - days.length;

    for (let day = 1; day <= remainingCells; day++) {
      const date = new Date(year, month + 1, day);
      days.push({
        date,
        day,
        isCurrentMonth: false,
        dateString: createDateString(year, month + 1, day),
      });
    }

    return days;
  };

  const calendarDays = getCalendarDays();

  // Format month/year for display
  const monthYear = currentDate.toLocaleDateString(i18n.language, {
    month: 'long',
    year: 'numeric'
  });

  // First attempts headline the pill, retries trail it as a dimmed secondary figure
  const renderPillCount = (breakdown: Record<number, number>) => {
    const { first, retries } = splitBreakdown(breakdown);

    return (
      <>
        {first}
        {retries > 0 && <span className="ml-1 font-normal opacity-75">+{retries}</span>}
      </>
    );
  };

  const renderAttemptSplit = (dayData: CalendarDayData) => {
    const split = splitDayAttempts({
      successful: dayData.successful_payments.retry_breakdown,
      preauth: dayData.preauth_payments.retry_breakdown,
      failed: dayData.failed_payments.retry_breakdown,
    });

    return (
      <div className="space-y-0.5">
        <p>
          {t('approvalRates.firstAttempt')}: {formatArm(split.firstAttempt)}
        </p>
        <p className="opacity-80">
          {t('calendar.retries')}: {formatArm(split.retries)}
        </p>
      </div>
    );
  };

  // Render a single calendar cell
  const renderCalendarCell = (dayInfo: CalendarDayInfo) => {
    const dayData = calendarData.get(dayInfo.dateString);
    const today = new Date();
    const todayString = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    const isToday = dayInfo.dateString === todayString;
    const hasData = dayData && dayInfo.isCurrentMonth && (
      (visibleMetrics.upcoming_billing && dayData.upcoming_billing.count > 0) ||
      (visibleMetrics.planned_cancellations && dayData.planned_cancellations.count > 0) ||
      (visibleMetrics.successful_payments && dayData.successful_payments.count > 0) ||
      (visibleMetrics.preauth_payments && dayData.preauth_payments.count > 0) ||
      (visibleMetrics.failed_payments && dayData.failed_payments.count > 0) ||
      (visibleMetrics.subscription_canceled && dayData.subscription_canceled.count > 0)
    );

    return (
      <div
        key={dayInfo.dateString}
        onClick={() => handleCellClick(dayInfo.dateString, dayInfo.isCurrentMonth)}
        className={`
          relative p-2.5 h-full min-h-[120px] border-r border-b border-gray-200 dark:border-gray-800 last:border-r-0
          ${dayInfo.isCurrentMonth
            ? 'bg-white dark:bg-gray-950 hover:bg-gray-50 dark:hover:bg-gray-900/50'
            : 'bg-gray-50/50 dark:bg-gray-900/20'
          }
          ${isToday ? 'ring-1 ring-inset ring-blue-400 dark:ring-blue-500 bg-blue-50/50 dark:bg-blue-950/20' : ''}
          ${hasData ? 'cursor-pointer hover:shadow-sm' : 'cursor-default'}
          transition-all duration-200 flex flex-col
        `}
      >
        {/* Day number */}
        <div className={`
          text-sm font-semibold mb-1.5 flex-shrink-0
          ${dayInfo.isCurrentMonth
            ? 'text-gray-900 dark:text-gray-100'
            : 'text-gray-400 dark:text-gray-600'
          }
          ${isToday ? 'text-blue-600 dark:text-blue-400' : ''}
        `}>
          {dayInfo.day}
        </div>

        {/* Metrics */}
        {dayData && dayInfo.isCurrentMonth && (
          <div className="flex-1 flex flex-col gap-1.5 min-h-0 overflow-y-auto scrollbar-thin">
            {/* Upcoming billing */}
            {visibleMetrics.upcoming_billing && dayData.upcoming_billing.count > 0 && (
              <div className="bg-emerald-500 text-white text-[10px] font-semibold px-2 py-1 rounded-md text-center leading-tight flex-shrink-0 shadow-sm">
                {dayData.upcoming_billing.count}
              </div>
            )}

            {/* Successful payments */}
            {visibleMetrics.successful_payments && dayData.successful_payments.count > 0 && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="bg-sky-500 text-white text-[10px] font-semibold px-2 py-1 rounded-md text-center leading-tight cursor-help flex-shrink-0 shadow-sm">
                    {renderPillCount(dayData.successful_payments.retry_breakdown)}
                  </div>
                </TooltipTrigger>
                <TooltipContent className="text-xs bg-gray-900 dark:bg-gray-800 text-white border-gray-700">
                  {renderAttemptSplit(dayData)}
                </TooltipContent>
              </Tooltip>
            )}

            {/* Planned cancellations */}
            {visibleMetrics.planned_cancellations && dayData.planned_cancellations.count > 0 && (
              <div className="bg-violet-500 text-white text-[10px] font-semibold px-2 py-1 rounded-md text-center leading-tight flex-shrink-0 shadow-sm">
                {dayData.planned_cancellations.count}
              </div>
            )}

            {/* PreAuth payments */}
            {visibleMetrics.preauth_payments && dayData.preauth_payments.count > 0 && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="bg-amber-500 text-white text-[10px] font-semibold px-2 py-1 rounded-md text-center leading-tight cursor-help flex-shrink-0 shadow-sm">
                    {renderPillCount(dayData.preauth_payments.retry_breakdown)}
                  </div>
                </TooltipTrigger>
                <TooltipContent className="text-xs bg-gray-900 dark:bg-gray-800 text-white border-gray-700">
                  {renderAttemptSplit(dayData)}
                </TooltipContent>
              </Tooltip>
            )}

            {/* Failed payments */}
            {visibleMetrics.failed_payments && dayData.failed_payments.count > 0 && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="bg-rose-500 text-white text-[10px] font-semibold px-2 py-1 rounded-md text-center leading-tight cursor-help flex-shrink-0 shadow-sm">
                    {renderPillCount(dayData.failed_payments.retry_breakdown)}
                  </div>
                </TooltipTrigger>
                <TooltipContent className="text-xs bg-gray-900 dark:bg-gray-800 text-white border-gray-700">
                  {renderAttemptSplit(dayData)}
                </TooltipContent>
              </Tooltip>
            )}

            {/* Subscription canceled */}
            {visibleMetrics.subscription_canceled && dayData.subscription_canceled.count > 0 && (
              <div className="bg-gray-500 text-white text-[10px] font-semibold px-2 py-1 rounded-md text-center leading-tight flex-shrink-0 shadow-sm">
                {dayData.subscription_canceled.count}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  if (isError) {
    return (
      <Card className="h-full flex flex-col shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg">{t('calendar.title')}</CardTitle>
          <CardDescription className="text-xs">{t('calendar.description')}</CardDescription>
        </CardHeader>
        <CardContent className="flex-1 flex items-center justify-center">
          <div className="text-center py-12">
            <div className="w-12 h-12 rounded-full bg-rose-100 dark:bg-rose-950 flex items-center justify-center mx-auto mb-4">
              <XCircle className="w-6 h-6 text-rose-600 dark:text-rose-400" />
            </div>
            <p className="text-base font-medium text-gray-900 dark:text-gray-100 mb-1">{t('calendar.errorMessage')}</p>
            <p className="text-sm text-gray-500 dark:text-gray-400 max-w-sm">
              {error instanceof Error ? error.message : 'Unable to load calendar data. Please try again.'}
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <TooltipProvider>
      <Card className="h-full flex flex-col shadow-sm">
        <CardHeader className="pb-4 space-y-3">
        <div className="flex items-center justify-between gap-4">
          <div className="min-w-0 flex-shrink">
            <CardTitle className="text-lg">{t('calendar.title')}</CardTitle>
            <CardDescription className="text-xs">{t('calendar.fullDescription')}</CardDescription>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            <Button
              variant="outline"
              size="icon"
              onClick={goToPreviousMonth}
              disabled={isLoading}
              className="h-8 w-8"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <div className="min-w-[140px] text-center px-2">
              <span className="text-sm font-semibold">{monthYear}</span>
            </div>
            <Button
              variant="outline"
              size="icon"
              onClick={goToNextMonth}
              disabled={isLoading}
              className="h-8 w-8"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Legend - Modern pill-shaped buttons */}
        <div className="flex flex-wrap gap-2 text-xs">
          <button
            onClick={() => toggleMetricVisibility('upcoming_billing')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-emerald-200 dark:border-emerald-800 transition-all duration-200 ${
              visibleMetrics.upcoming_billing
                ? 'bg-emerald-50 dark:bg-emerald-950/50 text-emerald-700 dark:text-emerald-300 shadow-sm'
                : 'opacity-40 hover:opacity-70 text-gray-600 dark:text-gray-400'
            }`}
          >
            <div className="w-2.5 h-2.5 rounded-full bg-emerald-500"></div>
            <span className="text-xs font-medium">{t('calendar.upcoming')}</span>
          </button>
          <button
            onClick={() => toggleMetricVisibility('successful_payments')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-sky-200 dark:border-sky-800 transition-all duration-200 ${
              visibleMetrics.successful_payments
                ? 'bg-sky-50 dark:bg-sky-950/50 text-sky-700 dark:text-sky-300 shadow-sm'
                : 'opacity-40 hover:opacity-70 text-gray-600 dark:text-gray-400'
            }`}
          >
            <div className="w-2.5 h-2.5 rounded-full bg-sky-500"></div>
            <span className="text-xs font-medium">{t('calendar.successful')}</span>
          </button>
          <button
            onClick={() => toggleMetricVisibility('planned_cancellations')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-violet-200 dark:border-violet-800 transition-all duration-200 ${
              visibleMetrics.planned_cancellations
                ? 'bg-violet-50 dark:bg-violet-950/50 text-violet-700 dark:text-violet-300 shadow-sm'
                : 'opacity-40 hover:opacity-70 text-gray-600 dark:text-gray-400'
            }`}
          >
            <div className="w-2.5 h-2.5 rounded-full bg-violet-500"></div>
            <span className="text-xs font-medium">{t('calendar.willCancel')}</span>
          </button>
          <button
            onClick={() => toggleMetricVisibility('preauth_payments')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-amber-200 dark:border-amber-800 transition-all duration-200 ${
              visibleMetrics.preauth_payments
                ? 'bg-amber-50 dark:bg-amber-950/50 text-amber-700 dark:text-amber-300 shadow-sm'
                : 'opacity-40 hover:opacity-70 text-gray-600 dark:text-gray-400'
            }`}
          >
            <div className="w-2.5 h-2.5 rounded-full bg-amber-500"></div>
            <span className="text-xs font-medium">{t('calendar.preAuth')}</span>
          </button>
          <button
            onClick={() => toggleMetricVisibility('failed_payments')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-rose-200 dark:border-rose-800 transition-all duration-200 ${
              visibleMetrics.failed_payments
                ? 'bg-rose-50 dark:bg-rose-950/50 text-rose-700 dark:text-rose-300 shadow-sm'
                : 'opacity-40 hover:opacity-70 text-gray-600 dark:text-gray-400'
            }`}
          >
            <div className="w-2.5 h-2.5 rounded-full bg-rose-500"></div>
            <span className="text-xs font-medium">{t('calendar.failed')}</span>
          </button>
          <button
            onClick={() => toggleMetricVisibility('subscription_canceled')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-gray-200 dark:border-gray-700 transition-all duration-200 ${
              visibleMetrics.subscription_canceled
                ? 'bg-gray-50 dark:bg-gray-900/50 text-gray-700 dark:text-gray-300 shadow-sm'
                : 'opacity-40 hover:opacity-70 text-gray-600 dark:text-gray-400'
            }`}
          >
            <div className="w-2.5 h-2.5 rounded-full bg-gray-500"></div>
            <span className="text-xs font-medium">{t('calendar.canceled')}</span>
          </button>
        </div>
      </CardHeader>

      <CardContent className="flex-1 flex flex-col min-h-0 p-4 overflow-x-auto">
        {/* Days of week header */}
        <div className="grid grid-cols-7 gap-0 mb-2 flex-shrink-0 min-w-[640px] rounded-t-lg overflow-hidden">
          {(t('calendar.days', { returnObjects: true }) as string[]).map((day) => (
            <div
              key={day}
              className="py-2 text-center text-[11px] font-semibold text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-900/30 border-r border-gray-200 dark:border-gray-800 last:border-r-0"
            >
              {day}
            </div>
          ))}
        </div>

        {/* Calendar Grid */}
        <div className="flex-1 min-h-[600px] overflow-auto">
          {isLoading ? (
            <div className="h-full flex items-center justify-center">
              <Skeleton className="w-full h-full" />
            </div>
          ) : calendarData.size === 0 ? (
            <div className="h-full flex flex-col items-center justify-center gap-4 text-center p-6">
              <div className="w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center mb-2">
                <Calendar className="w-8 h-8 text-gray-400 dark:text-gray-600" />
              </div>
              <div>
                <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">{t('calendar.noEvents')}</p>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-2 max-w-md">
                  {t('calendar.noActivity', { currency: selectedCurrency, monthYear })}
                </p>
                {selectedStores.length === 0 && (
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                    {t('calendar.trySelectStores')}
                  </p>
                )}
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-7 auto-rows-fr border rounded-lg overflow-hidden min-h-[600px] min-w-[640px]">
              {calendarDays.map((dayInfo) => renderCalendarCell(dayInfo))}
            </div>
          )}
        </div>
      </CardContent>
      </Card>
    </TooltipProvider>
  );
}
